import { API_URL, MONITORING_API_URL, APP_VERSION } from '../config/connect';

export const MonitoringEventType = {
  pageView: 'pageView',
  search: 'search',
  categoryView: 'categoryView',
  login: 'login',
  logout: 'logout',
  registration: 'registration',
  advertise: 'advertise',
  balanceTopUp: 'balanceTopUp',
  payment: 'payment',
  appOpen: 'appOpen',
  appClose: 'appClose',
};

const STORAGE_KEYS = {
  USER_ID: 'monitoring_user_id',
  PHONE_NUMBER: 'monitoring_phone_number',
  DEVICE_ID: 'device_id',
  SESSION_ID: 'monitoring_session_id',
};

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_AT = 20;
const BATCH_URL = `${MONITORING_API_URL}/monitoring/events/batch`;

const uuid = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

const readOrCreate = (storage, key) => {
  try {
    let value = storage.getItem(key);
    if (!value) {
      value = uuid();
      storage.setItem(key, value);
    }
    return value;
  } catch {
    return uuid();
  }
};

class MonitoringService {
  constructor() {
    this.deviceId = readOrCreate(localStorage, STORAGE_KEYS.DEVICE_ID);
    this.sessionId = readOrCreate(sessionStorage, STORAGE_KEYS.SESSION_ID);
    this.userId = localStorage.getItem(STORAGE_KEYS.USER_ID) || null;
    this.phoneNumber = localStorage.getItem(STORAGE_KEYS.PHONE_NUMBER) || null;
    this.queue = [];
    setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
    localStorage.removeItem('monitoring_events_queue');
  }

  async fetchUserPhoneNumber() {
    try {
      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) return;

      const response = await fetch(`${API_URL}/api/v1/user/profile`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return;

      const data = await response.json();
      if (data?.user?.phone_number) this.setPhoneNumber(data.user.phone_number);
      if (data?.user?.id) this.setUserId(data.user.id.toString());
    } catch {
      // fire-and-forget
    }
  }

  setUserId(userId) {
    this.userId = userId;
    if (userId) localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
    else localStorage.removeItem(STORAGE_KEYS.USER_ID);
  }

  setPhoneNumber(phoneNumber) {
    this.phoneNumber = phoneNumber;
    if (phoneNumber) localStorage.setItem(STORAGE_KEYS.PHONE_NUMBER, phoneNumber);
    else localStorage.removeItem(STORAGE_KEYS.PHONE_NUMBER);
  }

  async sendEvent(event) {
    this.queue.push({
      ...event,
      platform: 'web',
      appVersion: APP_VERSION,
      sessionId: this.sessionId,
      deviceId: this.deviceId,
      userId: this.userId || undefined,
      phoneNumber: event.phoneNumber || this.phoneNumber || undefined,
      timestamp: new Date().toISOString(),
    });
    if (this.queue.length >= FLUSH_AT) this.flush();
  }

  flush({ beacon = false } = {}) {
    if (this.queue.length === 0) return;
    const body = JSON.stringify({ events: this.queue.splice(0) });

    // text/plain — CORS preflight-сыз кетеді, sendBeacon да осыны талап етеді
    if (beacon && navigator.sendBeacon?.(BATCH_URL, body)) return;
    fetch(BATCH_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body,
      keepalive: true,
    }).catch(() => {});
  }

  async trackPageView(pageName, data) {
    await this.sendEvent({
      eventType: MonitoringEventType.pageView,
      eventName: 'page_view',
      pageName,
      data,
    });
  }

  async trackSearch(query, categoryId = null) {
    await this.sendEvent({
      eventType: MonitoringEventType.search,
      eventName: 'search',
      searchQuery: query,
      categoryId: categoryId || undefined,
    });
  }

  async trackCategoryView(categoryId, categoryName = null) {
    await this.sendEvent({
      eventType: MonitoringEventType.categoryView,
      eventName: 'category_view',
      categoryId,
      data: categoryName ? { categoryName } : undefined,
    });
  }

  async trackLogin(phoneNumber, success, errorMessage = null) {
    if (success && phoneNumber) this.setPhoneNumber(phoneNumber);
    await this.sendEvent({
      eventType: MonitoringEventType.login,
      eventName: 'login',
      success,
      error: errorMessage || undefined,
      phoneNumber: phoneNumber || undefined,
    });
  }

  async trackLogout() {
    await this.sendEvent({
      eventType: MonitoringEventType.logout,
      eventName: 'logout',
    });
    this.flush();
    this.setUserId(null);
    this.setPhoneNumber(null);
  }

  async trackRegistration(phoneNumber, success, errorMessage = null) {
    if (success && phoneNumber) this.setPhoneNumber(phoneNumber);
    await this.sendEvent({
      eventType: MonitoringEventType.registration,
      eventName: 'registration',
      success,
      error: errorMessage || undefined,
      phoneNumber: phoneNumber || undefined,
    });
  }

  async trackAdvertise(adId, success, errorMessage = null, data = {}) {
    await this.sendEvent({
      eventType: MonitoringEventType.advertise,
      eventName: 'advertise',
      success,
      error: errorMessage || undefined,
      data: { adId, ...data },
    });
  }

  async trackBalanceTopUp(amount, currency, success = true, errorMessage = null) {
    await this.sendEvent({
      eventType: MonitoringEventType.balanceTopUp,
      eventName: 'balance_top_up',
      amount,
      currency,
      success,
      error: errorMessage || undefined,
    });
  }

  async trackAppOpen() {
    await this.sendEvent({
      eventType: MonitoringEventType.appOpen,
      eventName: 'app_open',
    });
  }

  async trackAppClose() {
    await this.sendEvent({
      eventType: MonitoringEventType.appClose,
      eventName: 'app_close',
    });
    this.flush({ beacon: true });
  }
}

const monitoringService = new MonitoringService();

export default monitoringService;
